import { useCallback, useEffect, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import {
  DataTable,
  type DataTablePageEvent,
  type DataTableRowClickEvent,
} from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getAuditClient } from "sj-ui-toolkit/src/client";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import type { AuditEvent, ListEventsOptions } from "sj-ui-toolkit/src/client/svcAudit";

const PAGE_SIZE = 100;
interface AuditLogFilters {
  action: string;
  category: string;
  severity: string;
  occurredAtStart: Date | null;
  occurredAtEnd: Date | null;
}

const EMPTY_FILTERS: AuditLogFilters = {
  action: "",
  category: "",
  severity: "",
  occurredAtStart: null,
  occurredAtEnd: null,
};

const formatDate = (value: string | undefined): string => {
  if (!value) return "--";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatValue = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value ?? null);

const formatActor = (event: AuditEvent): string => {
  const identity =
    event.actorDirectoryUuid ??
    event.apiTokenId ??
    event.aiAgent ??
    event.actorService ??
    "Unknown";
  const impersonation = event.impersonatedDirectoryUuid
    ? `, impersonating ${event.impersonatedDirectoryUuid}`
    : "";

  return `${event.actorType ?? "Unknown"}: ${identity}${impersonation}`;
};

function DetailField({ label, value }: { label: string; value: unknown }) {
  const displayValue = Array.isArray(value) ? value.join(", ") || "--" : formatValue(value);

  return (
    <div className="min-w-0">
      <dt className="text-sm font-semibold text-secondary">{label}</dt>
      <dd className="mt-1 break-words text-sm">{displayValue}</dd>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  const sectionId = `audit-detail-${title.toLowerCase().replaceAll(" ", "-")}`;

  return (
    <section aria-labelledby={sectionId}>
      <h2 id={sectionId} className="atlas-h3 mb-3 border-b pb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

const toQuery = (filters: AuditLogFilters): ListEventsOptions => ({
  pageSize: PAGE_SIZE,
  action: filters.action || undefined,
  category: filters.category || undefined,
  severity: filters.severity || undefined,
  occurredAtStart: filters.occurredAtStart?.toISOString(),
  occurredAtEnd: filters.occurredAtEnd?.toISOString(),
});

export default function AuditLog() {
  useBreadcrumb({
    label: "Audit Log",
    route: AdminAppRoutes.routes.auditLog.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const tenant = getCurrentSelectedTenant() ?? "";
  const pageTokens = useRef<Record<number, string | undefined>>({ 0: undefined });
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [filters, setFilters] = useState<AuditLogFilters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<AuditLogFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

  const loadPage = useCallback(
    async (pageNumber: number, nextFilters: AuditLogFilters) => {
      if (!tenant) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await getAuditClient().listEvents(tenant, {
          ...toQuery(nextFilters),
          pageToken: pageTokens.current[pageNumber],
        });
        if (error) throw error;
        setEvents(data?.events ?? []);
        setNextPageToken(data?.nextPageToken);
        if (data?.nextPageToken) pageTokens.current[pageNumber + 1] = data.nextPageToken;
      } catch (error) {
        console.error("Failed to load audit log:", error);
        setEvents([]);
        setNextPageToken(undefined);
        showToast({ severity: "error", detail: "Failed to load audit log" });
      } finally {
        setLoading(false);
      }
    },
    [showToast, tenant]
  );

  useEffect(() => {
    void loadPage(page, filters);
  }, [filters, loadPage, page]);

  const applyFilters = () => {
    pageTokens.current = { 0: undefined };
    setPage(0);
    setFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    pageTokens.current = { 0: undefined };
    setPage(0);
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  };

  const handlePage = (event: DataTablePageEvent) => {
    if (event.page === undefined || (event.page > page && !nextPageToken)) return;
    setPage(event.page);
  };

  const totalRecords = nextPageToken ? (page + 2) * PAGE_SIZE : page * PAGE_SIZE + events.length;

  return (
    <div data-testid="admin-audit-log-page" aria-labelledby="audit-log-heading">
      <h1 id="audit-log-heading" className="atlas-h1 mb-4">
        Audit Log
      </h1>

      <Card>
        <section aria-labelledby="audit-log-filters-heading" className="mb-5">
          <h2 id="audit-log-filters-heading" className="atlas-h3 mb-3">
            Filters
          </h2>
          <div className="flex flex-wrap items-end gap-3">
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-action">Action</label>
              <InputText
                id="audit-action"
                value={draftFilters.action}
                onChange={event => setDraftFilters({ ...draftFilters, action: event.target.value })}
              />
            </span>
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-category">Category</label>
              <InputText
                id="audit-category"
                value={draftFilters.category}
                onChange={event =>
                  setDraftFilters({ ...draftFilters, category: event.target.value })
                }
              />
            </span>
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-severity">Severity</label>
              <InputText
                id="audit-severity"
                value={draftFilters.severity}
                onChange={event =>
                  setDraftFilters({ ...draftFilters, severity: event.target.value })
                }
              />
            </span>
            <span className="flex flex-col gap-1">
              <label htmlFor="audit-date-range">Occurred between</label>
              <Calendar
                id="audit-date-range"
                value={
                  draftFilters.occurredAtStart
                    ? [draftFilters.occurredAtStart, draftFilters.occurredAtEnd]
                    : null
                }
                onChange={event => {
                  const range = event.value as Date[] | null;
                  setDraftFilters({
                    ...draftFilters,
                    occurredAtStart: range?.[0] ?? null,
                    occurredAtEnd: range?.[1] ?? null,
                  });
                }}
                selectionMode="range"
                readOnlyInput
                showIcon
                inputId="audit-date-range"
              />
            </span>
            <Button label="Apply filters" icon="pi pi-filter" onClick={applyFilters} />
            <Button
              label="Clear"
              icon="pi pi-filter-slash"
              severity="secondary"
              onClick={clearFilters}
            />
          </div>
        </section>

        {!tenant && <Message severity="warn" text="Select a tenant to view its audit log." />}
        {tenant && loading && (
          <div className="flex justify-center p-6" role="status" aria-label="Loading audit log">
            <ProgressSpinner aria-label="Loading audit log" />
          </div>
        )}
        {tenant && !loading && (
          <DataTable
            value={events}
            lazy
            paginator
            first={page * PAGE_SIZE}
            rows={PAGE_SIZE}
            totalRecords={totalRecords}
            onPage={handlePage}
            emptyMessage="No audit events found."
            selectionMode="single"
            onRowClick={(event: DataTableRowClickEvent) =>
              setSelectedEvent(event.data as AuditEvent)
            }
            rowHover
            dataKey="eventId"
          >
            <Column
              field="occurredAt"
              header="Occurred"
              body={(event: AuditEvent) => formatDate(event.occurredAt)}
            />
            <Column field="category" header="Category" />
            <Column field="action" header="Action" />
            <Column field="severity" header="Severity" />
            <Column
              field="actorType"
              header="Actor"
              body={(event: AuditEvent) => formatActor(event)}
            />
            <Column
              field="targetDisplayName"
              header="Target"
              body={(event: AuditEvent) =>
                event.targetDisplayName || `${event.targetType}:${event.targetId}`
              }
            />
          </DataTable>
        )}
      </Card>

      <Sidebar
        header="Audit event details"
        visible={selectedEvent !== null}
        onHide={() => setSelectedEvent(null)}
        position="right"
        className="w-full md:w-[42rem]"
        aria-label="Audit event details"
      >
        {selectedEvent && (
          <div className="flex flex-col gap-6" id="audit-event-details">
            <DetailSection title="Event">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Event ID" value={selectedEvent.eventId} />
                <DetailField label="Tenant" value={selectedEvent.tenant} />
                <DetailField label="Occurred" value={formatDate(selectedEvent.occurredAt)} />
                <DetailField label="Received" value={formatDate(selectedEvent.receivedAt)} />
                <DetailField label="Category" value={selectedEvent.category} />
                <DetailField label="Action" value={selectedEvent.action} />
                <DetailField label="Severity" value={selectedEvent.severity} />
                <DetailField label="Action type" value={selectedEvent.detailsActionType} />
              </dl>
            </DetailSection>

            <DetailSection title="Actor">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Type" value={selectedEvent.actorType} />
                <DetailField label="Directory UUID" value={selectedEvent.actorDirectoryUuid} />
                <DetailField label="API token ID" value={selectedEvent.apiTokenId} />
                <DetailField label="Service" value={selectedEvent.actorService} />
                <DetailField label="Auth source" value={selectedEvent.authSource} />
                <DetailField
                  label="Impersonated UUID"
                  value={selectedEvent.impersonatedDirectoryUuid}
                />
              </dl>
            </DetailSection>

            <DetailSection title="Target">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Type" value={selectedEvent.targetType} />
                <DetailField label="ID" value={selectedEvent.targetId} />
                <DetailField label="Display name" value={selectedEvent.targetDisplayName} />
              </dl>
            </DetailSection>

            <DetailSection title="Change details">
              <dl className="grid grid-cols-1 gap-4">
                <DetailField label="Field set" value={selectedEvent.detailsFieldSet} />
                <DetailField label="Changed fields" value={selectedEvent.detailsChangedFields} />
                <DetailField label="Affected IDs" value={selectedEvent.detailsAffectedIds} />
                <DetailField
                  label="Referenced user emails"
                  value={selectedEvent.detailsRefUserEmails}
                />
                <DetailField label="Reason" value={selectedEvent.detailsReason} />
              </dl>
            </DetailSection>

            {(selectedEvent.detailsBefore || selectedEvent.detailsAfter) && (
              <DetailSection title="Before and after">
                <div className="grid grid-cols-1 gap-4">
                  {selectedEvent.detailsBefore && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-secondary">Before</h3>
                      <pre className="overflow-auto rounded border p-3 text-xs whitespace-pre-wrap">
                        {formatValue(selectedEvent.detailsBefore)}
                      </pre>
                    </div>
                  )}
                  {selectedEvent.detailsAfter && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-secondary">After</h3>
                      <pre className="overflow-auto rounded border p-3 text-xs whitespace-pre-wrap">
                        {formatValue(selectedEvent.detailsAfter)}
                      </pre>
                    </div>
                  )}
                </div>
              </DetailSection>
            )}
          </div>
        )}
      </Sidebar>
    </div>
  );
}
